import { useEffect, useMemo, useState } from 'react';
import { Layers, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { NewItemDialog } from '@/components/inventory/NewItemDialog';
import { useItems, type Item } from '@/features/inventory/hooks';
import {
  ancestorIds,
  hasBomLines,
  useAddBomLines,
  type BomGraph,
} from '@/features/manufacturing/boms';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

type KindFilter = 'all' | 'raw_material' | 'raw_stock' | 'finished_good' | 'sub_assembly';

const KIND_FILTERS: Array<{ value: KindFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'raw_material', label: 'Components' },
  { value: 'sub_assembly', label: 'Sub-assemblies' },
  { value: 'raw_stock', label: 'Raw material' },
  { value: 'finished_good', label: 'Finished goods' },
];

const ANY_GROUP = '__any__';

export interface BomAssemblyTarget {
  itemId: string;
  sku: string;
  name: string;
  parentSku?: string | null;
}

interface Staged {
  quantity: string;
  uom: string;
}

interface AddBomComponentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: BomAssemblyTarget | null;
  graph: BomGraph | undefined;
  /** Shown above the picker when editing a sub-assembly nested in another BOM. */
  notice?: string;
}

/**
 * Catalog browser for building a BOM: filter the item master by type,
 * sub-assembly group (`model_filters`, e.g. "Base", "Bowl"), or text,
 * tick any number of items, set a quantity per assembly for each, and
 * add them in one go. Items that are already on the BOM, or that would
 * make the BOM circular, are shown but disabled.
 */
export function AddBomComponentsDialog({
  open,
  onOpenChange,
  target,
  graph,
  notice,
}: AddBomComponentsDialogProps) {
  const { data: items, isLoading } = useItems();
  const addLines = useAddBomLines();

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [group, setGroup] = useState<string>(ANY_GROUP);
  const [staged, setStaged] = useState<Map<string, Staged>>(new Map());
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [pendingNewItemId, setPendingNewItemId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setKind('all');
      setGroup(ANY_GROUP);
      setStaged(new Map());
      setPendingNewItemId(null);
    }
  }, [open]);

  const itemsById = useMemo(() => new Map((items ?? []).map((it) => [it.id, it])), [items]);

  const onBom = useMemo(() => {
    if (!target) return new Set<string>();
    return new Set(
      (graph?.byItemId.get(target.itemId)?.lines ?? []).map((l) => l.component_item_id),
    );
  }, [graph, target]);

  const blocked = useMemo(() => {
    if (!target) return new Set<string>();
    const set = ancestorIds(graph, target.itemId);
    set.add(target.itemId);
    return set;
  }, [graph, target]);

  const groups = useMemo(() => {
    const set = new Set<string>();
    for (const it of items ?? []) for (const g of it.model_filters ?? []) set.add(g);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((it) => {
      if (it.status === 'archived') return false;
      if (kind === 'sub_assembly') {
        if (!hasBomLines(graph, it.id)) return false;
      } else if (kind !== 'all') {
        if (it.kind !== kind && !(it.additional_kinds ?? []).includes(kind)) return false;
      }
      if (group !== ANY_GROUP && !(it.model_filters ?? []).includes(group)) return false;
      if (!q) return true;
      return (
        it.sku.toLowerCase().includes(q) ||
        it.name.toLowerCase().includes(q) ||
        (it.category ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, kind, group, query, graph]);

  const selectable = useMemo(
    () => filtered.filter((it) => !blocked.has(it.id) && !onBom.has(it.id)),
    [filtered, blocked, onBom],
  );
  const allShownStaged = selectable.length > 0 && selectable.every((it) => staged.has(it.id));

  const stage = (it: Item) =>
    setStaged((prev) => {
      if (prev.has(it.id)) return prev;
      const next = new Map(prev);
      next.set(it.id, { quantity: '1', uom: it.base_uom || 'each' });
      return next;
    });

  const unstage = (id: string) =>
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  const toggleAllShown = () =>
    setStaged((prev) => {
      const next = new Map(prev);
      if (allShownStaged) {
        for (const it of selectable) next.delete(it.id);
      } else {
        for (const it of selectable) {
          if (!next.has(it.id)) next.set(it.id, { quantity: '1', uom: it.base_uom || 'each' });
        }
      }
      return next;
    });

  const patchStaged = (id: string, patch: Partial<Staged>) =>
    setStaged((prev) => {
      const current = prev.get(id);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(id, { ...current, ...patch });
      return next;
    });

  useEffect(() => {
    if (!pendingNewItemId) return;
    const created = itemsById.get(pendingNewItemId);
    if (created) {
      stage(created);
      setPendingNewItemId(null);
    }
  }, [pendingNewItemId, itemsById]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target || staged.size === 0) return;
    const lines = [...staged.entries()].map(([componentItemId, s]) => ({
      componentItemId,
      quantity: Number(s.quantity),
      uom: s.uom,
    }));
    if (lines.some((l) => !Number.isFinite(l.quantity) || l.quantity <= 0)) {
      toast.error('Every quantity must be greater than zero.');
      return;
    }
    try {
      const { added, skipped } = await addLines.mutateAsync({
        assemblyItemId: target.itemId,
        assemblyName: target.name,
        lines,
      });
      toast.success(
        `Added ${added} component${added === 1 ? '' : 's'} to ${target.sku}` +
          (skipped ? ` (${skipped} already on the BOM)` : ''),
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the BOM.');
    }
  };

  const stagedEntries = [...staged.entries()];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Add components</DialogTitle>
              {target && (
                <DialogDescription>
                  <span className="font-medium text-foreground">{target.name}</span>{' '}
                  <span className="font-mono text-xs">({target.sku})</span>
                </DialogDescription>
              )}
            </DialogHeader>

            {notice && (
              <p className="rounded-md border border-fresh-salmon/60 bg-fresh-salmon/15 px-3 py-2 text-xs text-fresh-salmon-dark">
                {notice}
              </p>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {KIND_FILTERS.map((k) => (
                    <button
                      key={k.value}
                      type="button"
                      onClick={() => setKind(k.value)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        kind === k.value
                          ? 'border-info bg-info text-info-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="flex flex-1 items-center gap-2 rounded-md border border-border px-3 py-2">
                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by SKU, name, or category…"
                      className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                  </div>
                  <Select value={group} onValueChange={setGroup}>
                    <SelectTrigger className="h-auto w-40" aria-label="Sub-assembly group">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY_GROUP}>Any group</SelectItem>
                      {groups.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
                  <span>
                    {filtered.length} item{filtered.length === 1 ? '' : 's'}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="font-medium text-info hover:underline disabled:opacity-50"
                      onClick={toggleAllShown}
                      disabled={selectable.length === 0}
                    >
                      {allShownStaged ? 'Clear shown' : `Select all shown (${selectable.length})`}
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-medium text-info hover:underline"
                      onClick={() => setNewItemOpen(true)}
                    >
                      <Plus className="h-3 w-3" />
                      New catalog item
                    </button>
                  </div>
                </div>
                <div className="h-[22rem] overflow-y-auto rounded-md border border-border">
                  {isLoading ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                      Loading catalog…
                    </div>
                  ) : filtered.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                      No matching items.
                    </div>
                  ) : (
                    filtered.map((it) => {
                      const isBlocked = blocked.has(it.id);
                      const isOnBom = onBom.has(it.id);
                      const disabled = isBlocked || isOnBom;
                      const isStaged = staged.has(it.id);
                      return (
                        <div
                          key={it.id}
                          role="button"
                          tabIndex={disabled ? -1 : 0}
                          aria-disabled={disabled}
                          onClick={() => !disabled && (isStaged ? unstage(it.id) : stage(it))}
                          onKeyDown={(e) => {
                            if (disabled) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (isStaged) unstage(it.id);
                              else stage(it);
                            }
                          }}
                          className={cn(
                            'flex w-full items-center gap-2.5 border-b border-border/60 px-3 py-1.5 text-left text-sm last:border-b-0',
                            disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer hover:bg-secondary/60',
                            isStaged && 'bg-info-soft/20',
                          )}
                        >
                          <Checkbox
                            checked={isStaged}
                            disabled={disabled}
                            onCheckedChange={(c) => (c ? stage(it) : unstage(it.id))}
                            ariaLabel={`Select ${it.sku}`}
                          />
                          <span className="w-40 shrink-0 truncate font-mono text-xs text-muted-foreground">
                            {it.sku}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{it.name}</span>
                          {hasBomLines(graph, it.id) && (
                            <Layers
                              className="h-3.5 w-3.5 shrink-0 text-info"
                              aria-label="Has its own BOM"
                            />
                          )}
                          {isOnBom ? (
                            <Badge variant="muted" className="shrink-0">
                              On BOM
                            </Badge>
                          ) : isBlocked ? (
                            <Badge variant="muted" className="shrink-0">
                              {it.id === target?.itemId ? 'This item' : 'Parent'}
                            </Badge>
                          ) : (
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                              {formatCurrency(it.std_cost_cents)}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex flex-col rounded-md border border-border bg-secondary/20">
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                  <span className="text-sm font-medium">
                    To add{' '}
                    <span className="tabular-nums text-muted-foreground">({staged.size})</span>
                  </span>
                  {staged.size > 0 && (
                    <button
                      type="button"
                      className="text-xs font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => setStaged(new Map())}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="max-h-[26rem] min-h-[10rem] flex-1 overflow-y-auto">
                  {stagedEntries.length === 0 ? (
                    <p className="px-4 py-10 text-center text-xs text-muted-foreground">
                      No components staged.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border/60">
                      {stagedEntries.map(([id, s]) => {
                        const it = itemsById.get(id);
                        return (
                          <li key={id} className="flex items-center gap-2 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm">{it?.name ?? 'Loading…'}</div>
                              <div className="truncate font-mono text-[11px] text-muted-foreground">
                                {it?.sku}
                              </div>
                            </div>
                            <Input
                              value={s.quantity}
                              onChange={(e) => patchStaged(id, { quantity: e.target.value })}
                              inputMode="decimal"
                              aria-label={`Quantity for ${it?.sku ?? 'item'}`}
                              className="h-8 w-16 text-right tabular-nums"
                            />
                            <Input
                              value={s.uom}
                              onChange={(e) => patchStaged(id, { uom: e.target.value })}
                              aria-label={`Unit of measure for ${it?.sku ?? 'item'}`}
                              maxLength={32}
                              className="h-8 w-16"
                            />
                            <button
                              type="button"
                              onClick={() => unstage(id)}
                              className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                              aria-label={`Remove ${it?.sku ?? 'item'}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={addLines.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addLines.isPending || staged.size === 0}>
                {addLines.isPending
                  ? 'Adding…'
                  : `Add ${staged.size} component${staged.size === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <NewItemDialog
        open={newItemOpen}
        onOpenChange={setNewItemOpen}
        defaultKind="raw_material"
        navigateOnCreate={false}
        onCreated={(id) => setPendingNewItemId(id)}
      />
    </>
  );
}
